import { datadogLogs } from "@datadog/browser-logs";
import { AuthenticationProvider, Util } from "@mindlercare/auth-ui";
import { ThemeProvider, SnackbarProvider } from "@mindlercare/ui-web";
import { UnleashProvider } from "@mindlercare/unleash-client";
import {
  ThemeProvider as MUIThemeProvider,
  StyledEngineProvider,
  Theme,
} from "@mui/material/styles";
import { MainErrorBoundary } from "components/errorBoundary";
import { DemoProvider } from "contexts/DemoContext";
import { blockLoginBasedOnRole } from "core/blockLogin";
import { PropsWithChildren, StrictMode, useCallback, useEffect } from "react";
import { createRoot } from "react-dom/client";
import { MindlerApiProviderV11 } from "./api/apiv2";
import { useGetMarkets } from "./api/markets";
import { App } from "./App";
import { auth } from "./services/auth";
import { GlobalStyle } from "./styles";
import { MUItheme } from "./styles/MUItheme";
import { version } from "../package.json";
import { useAppConfig } from "api/config";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { QueryClientProvider } from "@tanstack/react-query";
import { apiv2QueryClient } from "./apiv2QueryClient";
import { NuqsAdapter } from "nuqs/adapters/react";

declare module "@mui/styles/defaultTheme" {
  // eslint-disable-next-line @typescript-eslint/no-empty-object-type
  interface DefaultTheme extends Theme {}
}

const locale = navigator.language.split("-")[0];

const Configurator = ({ children }: PropsWithChildren) => {
  useGetMarkets(); // this sets things in localstorage etc so needs to be called once
  useAppConfig();

  return (
    <AuthenticationProvider
      auth={auth}
      locale={locale}
      blockLoginWhen={blockLoginBasedOnRole}
      logoutCallback={() => {
        Util.handleWebLogout();
      }}
    >
      {children}
    </AuthenticationProvider>
  );
};

const ThemeAndStyling = ({ children }: PropsWithChildren) => (
  <StyledEngineProvider injectFirst>
    <ThemeProvider>
      <MUIThemeProvider theme={MUItheme}>
        <GlobalStyle />
        {children}
      </MUIThemeProvider>
    </ThemeProvider>
  </StyledEngineProvider>
);

const unleashConfig = {
  url: import.meta.env.VITE_UNLEASH_URL,
  clientKey: import.meta.env.VITE_UNLEASH_CLIENTKEY,
  appName: "Mindlercare",
  environment: import.meta.env.VITE_STAGE,
};

const Main = () => {
  const handleNetworkStatusChange = useCallback(() => {
    const online = navigator.onLine;
    if (import.meta.env.VITE_LOG && !online) {
      console.warn("[ONLINE STATUS]", "offline");
    }
  }, []);

  useEffect(() => {
    window.addEventListener("offline", handleNetworkStatusChange);
    window.addEventListener("online", handleNetworkStatusChange);

    if (
      import.meta.env.VITE_DATADOG_CLIENT_TOKEN &&
      !datadogLogs.getInitConfiguration()
    ) {
      // Datadog only support a single session
      datadogLogs.init({
        clientToken: import.meta.env.VITE_DATADOG_CLIENT_TOKEN,
        site: "datadoghq.eu",
        service: "mindlercare",
        env: import.meta.env.VITE_DATADOG_ENV,
        version,
        forwardErrorsToLogs: true,
        sessionSampleRate: 100,
      });
    }

    return () => {
      window.removeEventListener("offline", handleNetworkStatusChange);
      window.removeEventListener("online", handleNetworkStatusChange);
    };
  }, [handleNetworkStatusChange]);

  return (
    <ThemeAndStyling>
      <MainErrorBoundary>
        <SnackbarProvider>
          <UnleashProvider config={unleashConfig}>
            <QueryClientProvider client={apiv2QueryClient}>
              <MindlerApiProviderV11 queryClient={apiv2QueryClient}>
                <Configurator>
                  <NuqsAdapter>
                    <DemoProvider>
                      <App />
                    </DemoProvider>
                  </NuqsAdapter>
                </Configurator>
                <ReactQueryDevtools initialIsOpen={false} />
              </MindlerApiProviderV11>
            </QueryClientProvider>
          </UnleashProvider>
        </SnackbarProvider>
      </MainErrorBoundary>
    </ThemeAndStyling>
  );
};

const container = document.getElementById("root");

const root = createRoot(container!);

root.render(
  <StrictMode>
    <Main />
  </StrictMode>,
);
